<template>
  <div id="#datasetVizSettings" class="mb-5" v-if="dataset.vizSettings">
    <p class="mb-0"><strong>Base Layer</strong></p>
    <b-btn
      @click="changeBaseLayer('satellite')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.baseLayer == 'satellite' }"
      class="mr-1 mt-1"
    >
      Satellite
    </b-btn>
    <b-btn
      @click="changeBaseLayer('light')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.baseLayer == 'light' }"
      class="mr-1 mt-1"
    >
      Light
    </b-btn>
    <b-btn
      @click="changeBaseLayer('dark')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.baseLayer == 'dark' }"
      class="mr-1 mt-1"
    >
      Dark
    </b-btn>

    <!-- Moved Farm Blocks Layer here -->
    <p class="mb-0 mt-4"><strong>Farm Blocks Layer</strong></p>
    <b-btn
      @click="changeShowBlocks(false)"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.showBlocks == false }"
      class="mr-1 mt-1"
    >
      Hide
    </b-btn>
    <b-btn
      @click="changeShowBlocks(true)"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.showBlocks == true }"
      class="mr-1 mt-1"
    >
      Show
    </b-btn>

    <!-- SUBLAYER -->
    <p class="mb-0 mt-4"><strong>Sub Layer</strong></p>
    <b-btn
      v-if="!dataset.vizSettings.subLayer"
      @click="$store.commit('setStateProperty', { property: 'subLayerPicker', value: true })"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.subLayer }"
      class="mr-1 mt-1"
    >
      Choose Dataset
    </b-btn>
    <!-- SELECTED DATASET -->
    <b-btn
      v-if="dataset.vizSettings.subLayer && !refreshingSubLayer"
      @click="refreshSubLayer()"
      variant="primary"
      size="sm"
      class="mb-1"
    >
      <i class="fas fa-sync-alt"></i> Refresh Sub Layer
    </b-btn>
    <b-btn
      v-if="dataset.vizSettings.subLayer && refreshingSubLayer"
      variant="primary"
      size="sm"
      class="mb-1 active"
    >
      <i class="fas fa-sync-alt fa-spin"></i> Refreshing...
    </b-btn>
    <div
      v-if="dataset.vizSettings.subLayer"
      class="list-group-item list-group-item-action mb-2 selected-dataset"
    >
      <p class="mb-0"><i class="fas fa-database"></i> {{ dataset.vizSettings.subLayer.name }} <i class="fas fa-arrow-circle-right clickable" @click="launchSubLayer()"></i></p>
      <i
        class="fas fa-times-circle clickable"
        id="remove-dataset"
        @click="removeSubLayer()"
      ></i>
    </div>

    <SubLayerPicker
      v-if="$store.state.subLayerPicker"
      v-on:updatedSubLayer="addSubLayer()"
    />

    <p
      class="mb-2 mt-4"
    ><strong>Opacity:</strong> {{ dataset.vizSettings.opacity }}</p>
    <vue-slider
      :height="6"
      :tooltip="'none'"
      :dotSize="30"
      v-on:drag-end="updateViz()"
      v-model="dataset.vizSettings.opacity"
      :min="0"
      :max="1"
      :interval="0.01"
    />

    <p class="mb-0 mt-4"><strong>Map Type</strong></p>
    <b-btn
      @click="changeVizType('circle')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.type == 'circle' }"
      class="mr-1 mt-1"
    >
      Point
    </b-btn>
    <b-btn
      @click="changeVizType('polygon')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.type == 'polygon' }"
      class="mr-1 mt-1"
    >
      Polygon
    </b-btn>
    <b-btn
      @click="changeVizType('line')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.type == 'line' }"
      class="mr-1 mt-1"
    >
      Line
    </b-btn>
    <b-btn
      @click="changeVizType('heatMap')"
      variant="primary"
      size="sm"
      :class="{ active: dataset.vizSettings.type == 'heatMap' }"
      class=" mt-1"
    >
      Heat Map
    </b-btn>

    <div v-if="dataset.vizSettings.type == 'circle' || dataset.vizSettings.type == 'polygon' || dataset.vizSettings.type == 'line'">
      <p class="mb-0 mt-4"><strong>Color Setting</strong></p>
      <b-btn
        @click="changeColorBy('solid')"
        variant="primary"
        size="sm"
        :class="{ active: dataset.vizSettings.colorBy == 'solid' }"
        class="mr-1 mt-1"
      >
        Solid
      </b-btn>
      <b-btn
        @click="changeColorBy('valueBased')"
        variant="primary"
        size="sm"
        :class="{ active: dataset.vizSettings.colorBy == 'valueBased' }"
        class="mr-1 mt-1"
      >
        Value Based
      </b-btn>

      <!-- SOLID COLOR SETTINGS -->
      <p v-if="dataset.vizSettings.colorBy == 'solid'" class="mb-0 mt-4"><strong>Color</strong></p>
      <v-swatches
        swatches="text-advanced"
        v-model="dataset.vizSettings.circleColor"
        popover-x="left"
        v-on:input="updateSwatchColor()"
        v-if="dataset.vizSettings.colorBy == 'solid' && !dataset.vizSettings.categorical"
        v-on:open="preventSave = true"
        v-on:close="closeSwatch()"
      ></v-swatches>
      <!-- VALUE BASED COLOR SETTINGS -->
      <div v-if="dataset.vizSettings.colorBy == 'valueBased'">
        <p
          v-if="headerOptions.length"
          class="mb-0 mt-4"
        ><strong>Color by Header</strong></p>
        <p
          v-else-if="headersLoading"
          class="mb-0 mt-4 text-muted"
        ><i class="fas fa-spinner fa-spin mr-1"></i> Loading headers…</p>
        <div
          v-for="(header, index) in headerOptions"
          v-bind:key="index"
          class="btn-container"
        >
          <div
            v-if="!isHiddenHeader(header)"
            @click="setHeader(header)"
            class="btn btn-sm btn-primary mr-1 mt-1 clickable"
            :class="{'active':(header === dataset.vizSettings.colorHeader)}"
          >
            {{ header }}
          </div>
        </div>

        <!-- Number Ramp Min/Max Colors -->
        <div v-if="!showRampSettings" class="mt-3">
          <p class="lead"><i class="fas fa-spinner-third fa-spin"></i></p>
        </div>
        <div v-if="dataset.vizSettings.number && showRampSettings">
          <!-- ZONE SETTINGS -->
          <!-- ZONE CLASSIFICATION BUTTONS -->
          <div>
            <p class="mb-0 mt-4"><strong>Ramp Setting</strong></p>
            <b-btn
              v-for="classification in classifications"
              v-bind:key="classification.value"
              @click="updateZoneClassification(classification.value)"
              variant="primary"
              size="sm"
              :class="{ active: dataset.vizSettings.zoneClassification === classification.value }"
              class="mr-1 mt-1"
            >
              {{ classification.text }}
            </b-btn>
          </div>
          <!-- ZONE COUNT BUTTONS -->
          <div v-if="dataset.vizSettings.zoneClassification !== 'linear' && dataset.vizSettings.zoneClassification !== 'categorical'">
            <p class="mb-0 mt-4"><strong>Zone Count</strong></p>
            <b-btn
              v-for="zone in zones"
              v-bind:key="zone.value"
              @click="updateZoneCount(zone.value)"
              variant="primary"
              size="sm"
              :class="{ active: dataset.vizSettings.zoneStops === zone.value }"
              class="mr-1 mt-1"
            >
              {{ zone.value }}
            </b-btn>
          </div>
          <!-- <div class="form-row mt-2 ml-1"> -->
            <!-- <b-form-group id="zones-type" class="mr-1">
              <b-form-select
                id="classification"
                v-model="dataset.vizSettings.zoneClassification"
                :options="classifications"
                @change="updateZoneSettings()"
                required
              ></b-form-select>
            </b-form-group> -->
            <!-- <b-form-group
              v-if="dataset.vizSettings.zoneClassification !== 'linear' && dataset.vizSettings.zoneClassification !== 'categorical'"
              id="stops-group"
            >
              <b-form-select
                id="stops"
                v-model="dataset.vizSettings.zoneStops"
                :options="zones"
                @change="updateZoneSettings()"
                required
              ></b-form-select>
            </b-form-group> -->
          <!-- </div> -->
          <p
            v-if="dataset.vizSettings.zoneClassification == 'linear'"
            class="mb-0 mt-4"
          ><strong>Min/Max Colors</strong></p>
          <!-- MIN COLOR SETTING -->
          <v-swatches
            swatches="text-advanced"
            v-if="dataset.vizSettings.zoneClassification == 'linear'"
            v-model="dataset.vizSettings.minColor"
            popover-x="left"
            v-on:input="updateSwatchColor()"
            v-on:open="preventSave = true"
            v-on:close="closeSwatch()"
            class="mr-1"
          ></v-swatches>
          <!-- MAX COLOR SETTING -->
          <v-swatches
            swatches="text-advanced"
            v-if="dataset.vizSettings.zoneClassification == 'linear'"
            v-model="dataset.vizSettings.maxColor"
            popover-x="left"
            v-on:input="updateSwatchColor()"
            v-on:open="preventSave = true"
            v-on:close="closeSwatch()"
          ></v-swatches>
          <div v-if="dataset.vizSettings.zoneClassification != 'categorical' && dataset.vizSettings.zoneClassification != 'linear'">
            <!-- COLOR ZONES RAMP SWATCHES -->
            <p
              v-if="dataset.vizSettings.zoneClassification != 'linear'"
              class="mb-0 mt-4"
            >
              <strong>Zone Colors</strong>
            </p>

            <b-btn class="mb-2" v-if="!showPresets" @click="showPresets = true" size="sm" variant="primary"><i class="fas fa-paint-brush"></i> Presets</b-btn>
            <b-btn class="mb-2" v-if="showPresets" @click="showPresets = false" size="sm" variant="secondary"><i class="fas fa-times"></i> Close Presets</b-btn>

            <div v-if="showPresets" class="mb-2">
              <b-btn
                v-for="(colorPreset, i) in $store.state.colors"
                v-bind:key="i"
                @click="setColorPreset(colorPreset)"
                class="color-preset"
                size="sm"
                variant="primary"
              >
                <span
                v-for="(colorVal, i) in colorPreset"
                v-bind:key="i"
                :style="'background-color:' + colorVal"
                class="color-chip"
                ></span>
              </b-btn>
            </div>

            <div
              v-if="dataset.vizSettings.zoneClassification != 'linear' && !hideSwatches"
              class="row"
            >
              <div
                class="col-4"
                v-for="(color, i) in dataset.vizSettings.colors.slice(0, dataset.vizSettings.zoneStops)"
                v-bind:key="i"
              >
                <p class="mb-0">Zone {{ i + 1 }}</p>
                <v-swatches
                  swatches="text-advanced"
                  v-model="dataset.vizSettings.colors[i]"
                  popover-x="left"
                  v-on:input="changeJenksColors()"
                  v-on:open="preventSave = true"
                  v-on:close="closeSwatch()"
                ></v-swatches>
              </div><!-- /.col-4 -->
            </div><!-- /.row -->
          </div><!-- Number Ramp Colors -->
        </div>
        <div v-if="dataset.vizSettings.categorical && dataset.vizSettings.colorHeader">
          <p class="mb-0 mt-4"><strong>Category Colors</strong></p>
          <div class="row">
            <div
              class="col-4"
              v-for="(category, i) in dataset.vizSettings.featureCategories"
              v-bind:key="i"
            >
              <p class="mb-0 mt-1">{{ category.name }}</p>
              <v-swatches
                swatches="text-advanced"
                v-model="category.color"
                popover-x="left"
                v-on:input="changeColorSwatch(i)"
                v-on:open="preventSave = true"
                v-on:close="closeSwatch()"
              ></v-swatches>
            </div><!-- /.col-4 -->
          </div><!-- /.row -->
        </div><!-- Categorical Colors -->
      </div><!-- /Check if value based -->
      <div>
        <p class="mb-0 mt-4"><strong>{{ dataset.vizSettings.type === 'circle' ? 'Point Label' : 'Label' }} Setting</strong></p>
        <b-btn
          @click="changeLabelBy('noLabel')"
          variant="primary"
          size="sm"
          :class="{ active: dataset.vizSettings.labelBy === 'noLabel' }"
          class="mr-1 mt-1"
        >
          No Label
        </b-btn>
        <b-btn
          @click="changeLabelBy('headerValue')"
          variant="primary"
          size="sm"
          :class="{ active: dataset.vizSettings.labelBy === 'headerValue' }"
          class="mr-1 mt-1"
        >
          Label by Header
        </b-btn>
        <!-- <b-form-group>
          <b-form-radio
            v-on:change="changeLabelBy()"
            v-model="dataset.vizSettings.labelBy"
            name="label-points-by"
            value="noLabel"
          >No Label</b-form-radio>
          <b-form-radio
            v-on:change="changeLabelBy()"
            v-model="dataset.vizSettings.labelBy"
            name="label-points-by"
            value="headerValue"
          >Label by Header</b-form-radio>
        </b-form-group> -->
        <div v-if="!showLabelOptions" class="mt-3">
          <p class="lead"><i class="fas fa-spinner-third fa-spin"></i></p>
        </div>
        <div v-if="showLabelOptions && headerOptions.length && dataset.vizSettings.labelBy === 'headerValue'">
          <p
            v-if="headerOptions.length"
            class="mb-0 mt-4"
          ><strong>Label by Header</strong></p>
          <div
            v-for="(header, me) in headerOptions"
            v-bind:key="me"
            class="btn-container"
          >
            <div
              v-if="!isHiddenHeader(header)"
              class="btn btn-sm btn-primary mr-1 mt-1 clickable"
              @click="setLabelHeader(header)"
              :class="{'active':(header === dataset.vizSettings.labelHeader)}"
            >
              {{ header }}
            </div><!-- /.btn -->
          </div>
        </div><!-- /v-if -->

        <!-- Shared label styling (points / lines / polygons) -->
        <template v-if="dataset.vizSettings.labelBy === 'headerValue'">
          <p class="mb-0 mt-4"><strong>Label color</strong></p>
          <v-swatches
            swatches="text-advanced"
            v-model="dataset.vizSettings.labelColor"
            popover-x="left"
            v-on:input="updateSwatchColor()"
            v-on:open="preventSave = true"
            v-on:close="closeSwatch()"
          ></v-swatches>
          <p class="mb-2 mt-4"><strong>Label size:</strong> {{ dataset.vizSettings.labelSize }}</p>
          <vue-slider
            :height="6"
            :tooltip="'none'"
            :dotSize="30"
            v-on:drag-end="updateViz()"
            v-model="dataset.vizSettings.labelSize"
            :min="8"
            :max="40"
            :interval="1"
          />
        </template><!-- /label styling -->

        <template v-if="dataset.vizSettings.type === 'circle'">
          <p class="mb-0 mt-4"><strong>Point Size Setting</strong></p>
          <b-btn
            @click="changeSizeBy('fixed')"
          variant="primary"
          size="sm"
          :class="{ active: dataset.vizSettings.sizeBy === 'fixed' }"
          class="mr-1 mt-1"
        >
          Fixed
        </b-btn>
        <b-btn
          @click="changeSizeBy('valueBased')"
          variant="primary"
          size="sm"
          :class="{ active: dataset.vizSettings.sizeBy === 'valueBased' }"
          class="mr-1 mt-1"
        >
          Value Based
        </b-btn>
        <!-- <b-form-group>
          <b-form-radio
            v-on:change="updateViz()"
            v-model="dataset.vizSettings.sizeBy"
            name="size-points-by"
            value="fixed"
          >Fixed</b-form-radio>
          <b-form-radio
            v-on:change="updateViz()"
            v-model="dataset.vizSettings.sizeBy"
            name="size-points-by"
            value="valueBased"
          >Value Based</b-form-radio>
        </b-form-group> -->
        <p
          v-if="dataset.vizSettings.sizeBy == 'fixed'"
          class="mb-2 mt-4"
        ><strong>Point Size:</strong> {{ dataset.vizSettings.circleRadius }}</p>
        <vue-slider
          :height="6"
          :tooltip="'none'"
          :dotSize="30"
          v-if="dataset.vizSettings.sizeBy == 'fixed'"
          v-on:drag-end="updateViz()"
          v-model="dataset.vizSettings.circleRadius"
          :min="1"
          :max="50"
        />
        <div v-if="dataset.vizSettings.sizeBy == 'valueBased'">
          <p
            v-if="headerOptions.length"
            class="mb-0 mt-4"
          ><strong>Size by Header</strong></p>
          <div
            v-for="(header, index) in headerOptions"
            v-bind:key="index"
            class="btn-container"
          >
            <div
              v-if="!isHiddenHeader(header)"
              class="btn btn-sm btn-primary mt-1 mr-1 clickable"
              @click="setSizeHeader(header)"
              :class="{'active':(header === dataset.vizSettings.sizeHeader)}"
            >
              {{ header }}
            </div>
          </div>
          <p
            v-if="dataset.vizSettings.sizeCategorical"
            style="color:red;"
          >Only numerical headers can set sizes of points!</p>
          <p class="mb-2 mt-4"><strong>Min Point Size:</strong> {{ dataset.vizSettings.circleRadiusMin }}</p>
          <vue-slider
            :height="6"
            :tooltip="'none'"
            :dotSize="30"
            v-on:drag-end="updateViz()"
            v-model="dataset.vizSettings.circleRadiusMin"
            :min="1"
            :max="dataset.vizSettings.circleRadiusMax"
          />

          <p class="mb-2 mt-4"><strong>Max Point Size:</strong> {{ dataset.vizSettings.circleRadiusMax }}</p>
          <vue-slider
            :height="6"
            :tooltip="'none'"
            :dotSize="30"
            v-on:drag-end="updateViz()"
            v-model="dataset.vizSettings.circleRadiusMax"
            :min="dataset.vizSettings.circleRadiusMin"
            :max="50"
          />
        </div><!-- /if value based -->
        </template>

        <div v-if="dataset.vizSettings.type === 'line'" class="mt-4">
          <p class="mb-2"><strong>Line width:</strong> {{ dataset.vizSettings.lineWidth }}</p>
          <vue-slider
            :height="6"
            :tooltip="'none'"
            :dotSize="30"
            v-on:drag-end="updateViz()"
            v-model="dataset.vizSettings.lineWidth"
            :min="0.5"
            :max="10"
            :interval="0.5"
          />
        </div>

        <!-- Polygon border (color + width) -->
        <template v-if="dataset.vizSettings.type === 'polygon'">
          <p class="mb-0 mt-4"><strong>Border color</strong></p>
          <v-swatches
            swatches="text-advanced"
            v-model="dataset.vizSettings.borderColor"
            popover-x="left"
            v-on:input="updateSwatchColor()"
            v-on:open="preventSave = true"
            v-on:close="closeSwatch()"
          ></v-swatches>
          <p class="mb-2 mt-4"><strong>Border width:</strong> {{ dataset.vizSettings.borderWidth }}</p>
          <vue-slider
            :height="6"
            :tooltip="'none'"
            :dotSize="30"
            v-on:drag-end="updateViz()"
            v-model="dataset.vizSettings.borderWidth"
            :min="0"
            :max="10"
            :interval="0.5"
          />
        </template><!-- /polygon border -->
      </div><!-- /. label + style block -->
    </div><!-- if circle map -->
    <div v-if="dataset.vizSettings.type == 'heatMap'">
      <p class="mb-0 mt-2"><strong>Heat Setting</strong></p>

      <b-btn
        @click="changeHeatBy('standard')"
        variant="primary"
        size="sm"
        :class="{ active: dataset.vizSettings.heatBy == 'standard' }"
        class="ml-1"
      >
        Standard
      </b-btn>
      <b-btn
        @click="changeHeatBy('weighted')"
        variant="primary"
        size="sm"
        :class="{ active: dataset.vizSettings.heatBy == 'weighted' }"
        class="ml-1"
      >
        Weighted
      </b-btn>
      <!-- SETTINGS FOR WEIGHTED HEATMAPS -->
      <div v-if="dataset.vizSettings.heatBy == 'weighted'">
        <p
          v-if="headerOptions.length"
          class="mb-0 mt-4"
        ><strong>Header</strong></p>
        <div
          v-for="(header, index) in headerOptions"
          v-bind:key="index"
          class="btn-container"
        >
          <div
            v-if="!isHiddenHeader(header)"
            class="btn btn-sm btn-primary mt-1 mr-1 clickable"
            @click="setHeader(header)"
            :class="{'active':(header === dataset.vizSettings.colorHeader)}"
          >
            {{ header }}
          </div>
        </div>
        <p
          v-if="dataset.vizSettings.categorical"
          style="color:red;"
        >Only numerical headers can weight a heat map!</p>
      </div><!-- if heatBy is weighted -->
      <p class="mb-0 mt-2"><strong>Heat Radius:</strong> {{ dataset.vizSettings.heatRadius }}</p>
      <vue-slider
        :height="6"
        :tooltip="'none'"
        :dotSize="30"
        v-on:drag-end="updateViz()"
        v-model="dataset.vizSettings.heatRadius"
        :min="1"
        :max="50"
      />
    </div><!-- if heat map -->
    <DatasetFilters
      v-on:initViz="$store.dispatch('visualizeLayer', 'datasetPreview')"
      v-on:saveViz="saveViz()"
    />
    <div class="mobile-buffer"></div>
  </div><!-- /#datasetVizSettings -->
</template>

<script>
import VSwatches from 'vue-swatches'
import VueSlider from 'vue-slider-component'
// import jenks from 'turf-jenks'
import DatasetFilters from './DatasetFilters'
import SubLayerPicker from './SubLayerPicker'
import { deriveHeaders as deriveHeadersUnion, mergeHeaders } from '@/lib/deriveHeaders'
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      refreshingSubLayer: false,
      // Headers derived from the actual loaded feature data (the bulletproof
      // source for the color/size/label pickers — see ensureHeadersLoaded).
      liveHeaders: [],
      headersLoading: false,
      showLabelOptions: true,
      // showRampSettings: true,
      showPresets: false,
      hideSwatches: false,
      swatches: {
        complimentary: ['#F76A6A', '#AD2F2F', '#6AF792', '#87FFA9', '#38AB59']
      },
      preventSave: false,
      defaultViz: {
        showBlocks: false,
        baseLayer: 'satellite',
        type: 'circle',
        breaks: [],
        categorical: false,
        circleRadius: 4,
        circleRadiusMin: 5,
        circleRadiusMax: 20,
        circleColor: '#ffc107',
        colorHeader: false,
        colorBy: 'solid',
        colors: [
          // OLD DEFAULT ZONE COLOR SCHEME
          // '#e06666',
          // '#f6b26b',
          // '#ffd966',
          // '#94c47d',
          // '#70a8dc',
          // '#8e7cc3',
          // '#cc4125',
          // '#c27ba0',
          // '#77CCCC',
          // '#777711',
          // '#AA7744',
          // '#AAAA44',
          // '#774411',
          // '#4477AA',
          // '#AA4455',
          // '#771155',
          // '#DDDD77',
          // '#117744',
          // '#DDAA77',
          // '#CC99BB',
          // '#44AAAA',
          // '#117777',
          // '#77AADD',
          // '#88CCAA',
          // '#DD7788',
          // '#114477',
          // '#AA4488',
          // '#771122',
          // '#44AA77'
          '#953c22',
          // '#b65643',
          '#d67431',
          // '#eea82d',
          '#fbe735',
          // '#7ce92c',
          '#23ce34',
          // '#27a57e',
          '#1f738c',
          // '#1a3462',
          '#000018',
          '#e06666',
          '#f6b26b',
          '#ffd966',
          '#94c47d',
          '#70a8dc',
          '#8e7cc3',
          '#cc4125',
          '#c27ba0',
          '#77CCCC',
          '#777711',
          '#AA7744',
          '#AAAA44',
          '#774411',
          '#4477AA',
          '#AA4455',
          '#771155',
          '#DDDD77',
          '#117744',
          '#DDAA77',
          '#CC99BB',
          '#44AAAA',
          '#117777',
          '#77AADD',
          '#88CCAA',
          '#DD7788',
          '#114477',
          '#AA4488',
          '#771122',
          '#44AA77'
        ],
        featureCategories: [],
        filters: [],
        heatBy: 'standard',
        heatRadius: 30,
        labelBy: 'noLabel',
        labelHeader: false,
        minValue: false,
        maxValue: false,
        minSizeValue: false,
        maxSizeValue: false,
        minColor: '#ffc107',
        maxColor: '#056839',
        number: true,
        opacity: 1,
        recalculateRange: true,
        recalculateJenks: true,
        zoneClassification: 'linear',
        zoneStops: 5,
        sizeCategorical: false,
        sizeNumber: true,
        sizeHeader: false,
        sizeBy: 'fixed',
        subLayer: false,
        lineWidth: 2
      },
      zones: [
        {
          text: '3 Zones',
          value: 3
        },
        {
          text: '4 Zones',
          value: 4
        },
        {
          text: '5 Zones',
          value: 5
        },
        {
          text: '6 Zones',
          value: 6
        },
        {
          text: '7 Zones',
          value: 7
        },
        {
          text: '8 Zones',
          value: 8
        },
        {
          text: '9 Zones',
          value: 9
        },
        {
          text: '10 Zones',
          value: 10
        }
      ],
      classifications: [
        {
          text: 'Linear',
          value: 'linear'
        },
        {
          text: 'Equal interval',
          value: 'equal-interval'
        },
        {
          text: 'Jenks',
          value: 'jenks'
        },
        {
          text: 'Categorical',
          value: 'categorical'
        }
      ]
    }
  },
  components: {
    SubLayerPicker,
    VSwatches,
    VueSlider,
    DatasetFilters
  },
  computed: {
    showRampSettings: {
      get () {
        return this.$store.state.showRampSettings
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'showRampSettings', value: value })
      }
    },
    datasetGeoJson: {
      get () {
        return this.$store.state.datasetGeoJson
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'datasetGeoJson', value: value })
      }
    },
    dataset: {
      get () {
        return this.$store.state.dataset
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'dataset', value: value })
      }
    },
    // Header choices for the color/label/size/heat pickers.
    //
    // Bulletproofing: the dataset doc's `headers` can be empty or incomplete
    // on a dataset's first open (they're written asynchronously at creation,
    // and some creation paths persisted headers off features[0] only), which
    // left the picker empty until the dataset was reopened. So we PREFER
    // headers derived from the actual feature data — `liveHeaders` (loaded by
    // ensureHeadersLoaded) or the GeoJSON already in state — and only fall
    // back to the stored doc headers for an instant first paint while that
    // data loads. The data-derived union is always the most complete source.
    headerOptions () {
      // Prefer the dataset doc's stored headers — they are reliably populated
      // now (the API derives them as a union across all features, and the
      // dataset launch backfills them from the GeoJSON). Only when they're
      // genuinely absent (legacy/edge datasets) do we fall back to headers
      // derived from the loaded feature data (liveHeaders, filled by
      // ensureHeadersLoaded). Preferring stored avoids two failure modes:
      // showing nothing while the (cold) GeoJSON fetch is still in flight, and
      // briefly showing the PREVIOUS dataset's headers from a stale
      // state.datasetGeoJson when switching datasets.
      const stored = (this.dataset && Array.isArray(this.dataset.headers)) ? this.dataset.headers : []
      // Dynamic datasets: the doc is authoritative (collector fields + CV
      // class headers, some with no data yet).
      if (this.dataset && this.dataset.dynamic) {
        return stored.length ? stored : this.liveHeaders
      }
      // Static datasets: the stored list can be partial (older docs were
      // written from features[0] only) — show the UNION of stored and
      // data-derived so the picker is complete the first time, every time.
      const live = this.liveHeaders.length
        ? this.liveHeaders
        : this.deriveHeaders(this.$store.state.datasetGeoJson)
      return mergeHeaders(stored, live)
    },
    mapObject: {
      get () {
        return this.$store.state.mapObject
      },
      set (value) {
        this.$store.commit('setMapObject', value)
      }
    }
  },
  created () {
    // IF THIS IS A DYNAMIC DATASET, CHECK FOR ANY CV HEADERS, REMOVE THEM, AND ADD BACK THE CLASS HEADERS ('FIELD LABEL' + ' - ' + 'CLASSNAME')
    if (this.dataset.dynamic) {
      this.$store.state.collector.fields.forEach(field => {
        if (field.type === 'Computer Vision') {
          console.log('Computer Vision')
          this.datasetGeoJson.features.forEach(feature => {
            const keys = Object.keys(feature.properties)
            keys.forEach(keyString => {
              if (keyString.startsWith(field.label)) {
                if (!this.dataset.headers.includes(keyString)) {
                  console.log('New Header! ' + keyString)
                  this.dataset.headers.push(keyString)
                }
              }
            })
          })
        }
      })
    }
    // Check that default viz properties all exist. Never attach or add keys
    // in place: Vue 2 can't observe properties added to an already-observed
    // object, which is exactly how the panel ended up not re-rendering until
    // a refresh (see launchDataset in mapActions.js). Build a merged copy and
    // replace the dataset object through the store instead.
    {
      const existing = this.dataset.vizSettings || {}
      const merged = { ...existing }
      let changed = !this.dataset.vizSettings
      Object.keys(this.defaultViz).forEach(key => {
        if (merged[key] === 'undefined' || (!merged[key] && merged[key] !== false)) {
          merged[key] = this.defaultViz[key]
          changed = true
        }
      })
      if (changed) {
        this.dataset = { ...this.dataset, vizSettings: merged }
      }
    }
    // Check if we're dealing with a polygon layer and update vizSettings accordingly
    if (this.datasetGeoJson && this.datasetGeoJson.features && this.datasetGeoJson.features.length > 0 && this.datasetGeoJson.features[0].geometry && this.datasetGeoJson.features[0].geometry.type === 'Polygon') {
      this.dataset.vizSettings.type = 'polygon'
    }
    // Check if we're dealing with a line layer and update vizSettings accordingly
    if (this.datasetGeoJson && this.datasetGeoJson.features && this.datasetGeoJson.features.length > 0 && this.datasetGeoJson.features[0].geometry && (this.datasetGeoJson.features[0].geometry.type === 'LineString' || this.datasetGeoJson.features[0].geometry.type === 'MultiLineString')) {
      this.dataset.vizSettings.type = 'line'
    }
    // if (this.dataset.vizSettings.recalculateJenks) {
    //   this.dataset.vizSettings.recalculateJenks = false
    // }
    setTimeout(() => {
      this.$store.commit('resetKey', 'dataset')
    }, 1000)
    // Load header options straight from the feature data (see ensureHeadersLoaded).
    this.ensureHeadersLoaded()
  },
  watch: {
    // The drawer can stay mounted while the user switches datasets — reset the
    // derived headers and reload them whenever a different dataset loads in.
    'dataset.id' () {
      this.liveHeaders = []
      this.ensureHeadersLoaded()
    }
  },
  methods: {
    // Union of property keys across the GeoJSON's features (first 100).
    // Deliberately NOT first-feature-only: a first feature with empty
    // properties {} would otherwise yield no headers even though the rest
    // of the data has them — the exact hole in the header extraction at
    // dataset creation (CreateDataset.vue and the API both key off
    // features[0]).
    deriveHeaders (geojson) {
      // Shared implementation (src/lib/deriveHeaders.js): union across ALL
      // features, same as creation, launch, the API and every plugin.
      return deriveHeadersUnion(geojson)
    },
    // Bulletproof header loading for the pickers. Rather than trusting the
    // dataset doc's `headers` field (which can be empty/incomplete/stale on a
    // dataset's first open), this derives headers straight from the feature
    // data at the point of use:
    //   1. derive from the GeoJSON already in state (instant, no network), else
    //   2. fetch the dataset's GeoJSON file from storage and derive from it.
    // The result is published to `liveHeaders` (so the picker fills in even
    // when the doc had none), the GeoJSON is cached into state, and the doc is
    // backfilled so other consumers (list, API) see correct headers too.
    // `headersLoading` drives a spinner so the picker never just looks empty.
    async ensureHeadersLoaded () {
      const d = this.dataset
      if (!d || !d.id || !d.file || d.file.raster) return
      // 1. Already have it from the data in state? Use it immediately.
      const inState = this.deriveHeaders(this.$store.state.datasetGeoJson)
      if (inState.length) {
        this.liveHeaders = inState
      }
      // 2. Otherwise fetch the file (skip for dynamic datasets — their state
      //    GeoJSON is populated on launch and headers come from the collector).
      if (!inState.length && !d.dynamic && d.file.geoJsonPath) {
        this.headersLoading = true
        try {
          const url = await fb.storage.ref(d.file.geoJsonPath).getDownloadURL()
          const geojson = await (await fetch(url)).json()
          const headers = this.deriveHeaders(geojson)
          // Guard against the user having switched datasets mid-fetch.
          if (this.dataset && this.dataset.id === d.id && headers.length) {
            this.liveHeaders = headers
            const stateGeo = this.$store.state.datasetGeoJson
            if (!stateGeo || !stateGeo.features || !stateGeo.features.length) {
              this.$store.commit('setStateProperty', { property: 'datasetGeoJson', value: geojson })
            }
          }
        } catch (e) {
          console.log('ensureHeadersLoaded: could not load GeoJSON for header derivation:', e && e.message)
        } finally {
          this.headersLoading = false
        }
      }
      // Backfill the doc when it has no headers, so subsequent loads and other
      // consumers don't depend on this rescue. Never shrink an existing set.
      const derived = this.liveHeaders
      const stored = (this.dataset && this.dataset.headers) || []
      if (derived.length && derived.length > stored.length && this.dataset.id === d.id && !d.dynamic) {
        this.$store.commit('setStateProperty', { property: 'dataset', value: { ...this.dataset, headers: derived } })
        fb.datasetsCollection.doc(d.id).update({ headers: derived }).catch(() => {})
      }
    },
    changeShowBlocks (value) {
      this.dataset.vizSettings.showBlocks = value
      this.updateViz()
      // this.$store.dispatch('visualizeLayer', 'datasetPreview')
      // this.saveViz()
    },
    async launchSubLayer () {
      const sub = this.dataset.vizSettings && this.dataset.vizSettings.subLayer
      if (!sub || !sub.id) return
      const subLayerDoc = await fb.datasetsCollection.doc(sub.id).get()
      const subLayerData = subLayerDoc.data()
      subLayerData.id = subLayerDoc.id
      this.$store.commit('setStateProperty', { property: 'pauseMap', value: false })
      this.$store.dispatch('launchDataset', { dataset: subLayerData })
    },
    async refreshSubLayer () {
      const sub = this.dataset.vizSettings && this.dataset.vizSettings.subLayer
      if (!sub || !sub.id) return
      this.refreshingSubLayer = true
      const subLayerId = sub.id
      const subLayerDoc = await fb.datasetsCollection.doc(subLayerId).get()
      this.dataset.vizSettings.subLayer = subLayerDoc.data()
      this.dataset.vizSettings.subLayer.id = subLayerDoc.id
      this.$store.dispatch('visualizeLayer', 'subLayer')
      this.refreshingSubLayer = false
      this.saveViz()
    },
    addSubLayer () {
      this.$store.dispatch('mapDataset', this.dataset)
      this.saveViz()
    },
    removeSubLayer () {
      this.dataset.vizSettings.subLayer = false
      this.$store.dispatch('mapDataset', this.dataset)
      this.saveViz()
    },
    updateViz () {
      setTimeout(() => {
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    updateSwatchColor () {
      setTimeout(() => {
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    changeSizeBy (value) {
      this.dataset.vizSettings.sizeBy = value
      this.$store.dispatch('visualizeLayer', 'datasetPreview')
      this.saveViz()
    },
    changeColorBy (value) {
      this.dataset.vizSettings.colorBy = value
      // Guarantee the header picker has options the moment Value Based is
      // chosen — load them from the feature data if we don't have them yet.
      if (value === 'valueBased' && !this.headerOptions.length) {
        this.ensureHeadersLoaded()
      }
      this.$store.dispatch('visualizeLayer', 'datasetPreview')
      this.saveViz()
    },
    isHiddenHeader (val) {
      if (this.dataset.dynamic) {
        let isHidden = false
        this.$store.state.collector.fields.forEach(header => {
          if (header.hidden && header.label === val) {
            isHidden = true
          }
        })
        return isHidden
      } else {
        return false
      }
    },
    setLabelHeader (header) {
      this.dataset.vizSettings.labelHeader = header
      this.showLabelOptions = false
      setTimeout(() => {
        this.showLabelOptions = true
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    setColorPreset (colorPreset) {
      this.hideSwatches = true
      for (let i = 0; i < this.dataset.vizSettings.zoneStops; i++) {
        const iterator = colorPreset.length / this.dataset.vizSettings.zoneStops
        const colorPresetIndex = Math.round(iterator * i)
        this.dataset.vizSettings.colors[i] = colorPreset[colorPresetIndex]
      }
      this.hideSwatches = false
      this.changeJenksColors()
    },
    setSizeHeader (header) {
      this.dataset.vizSettings.recalculateRange = true
      this.dataset.vizSettings.sizeHeader = header
      this.$store.dispatch('visualizeLayer', 'datasetPreview')
      this.saveViz()
    },
    changeLabelBy (value) {
      this.dataset.vizSettings.labelBy = value
      this.$store.dispatch('visualizeLayer', 'datasetPreview')
      this.saveViz()
      // this.showLabelOptions = false
      // setTimeout(() => {
      //   this.$store.dispatch('visualizeLayer', 'datasetPreview')
      //   this.saveViz()
      //   this.showLabelOptions = true
      // }, 100)
    },
    // changeSizeBy () {
    //   this.$store.dispatch('visualizeLayer', 'datasetPreview')
    //   this.saveViz()
    // },
    updateZoneClassification (classification) {
      this.dataset.vizSettings.zoneClassification = classification
      this.dataset.vizSettings.recalculateJenks = true
      setTimeout(() => {
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    updateZoneCount (count) {
      this.dataset.vizSettings.zoneStops = count
      this.dataset.vizSettings.recalculateJenks = true
      setTimeout(() => {
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    changeJenksColors () {
      this.dataset.vizSettings.recalculateJenks = false
      setTimeout(() => {
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    closeSwatch () {
      this.preventSave = false
    },
    changeHeatBy (value) {
      this.dataset.vizSettings.heatBy = value
      this.$store.dispatch('visualizeLayer', 'datasetPreview')
      this.saveViz()
    },
    changeColorSwatch (index) {
      this.dataset.vizSettings.colors[index] = this.dataset.vizSettings.featureCategories[index].color
      // Timeout lets the colorBy and heatBy values 'set' first by the radios which seem to
      // take their dandy old time
      setTimeout(() => {
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }, 100)
    },
    setHeader (header) {
      this.dataset.vizSettings.recalculateRange = true
      this.dataset.vizSettings.recalculateJenks = true
      this.dataset.vizSettings.colorHeader = header
      this.$store.dispatch('visualizeLayer', 'datasetPreview')
      this.saveViz()
    },
    changeVizType (type) {
      if (this.dataset.vizSettings.type !== type) {
        this.dataset.vizSettings.type = type
        this.$store.dispatch('visualizeLayer', 'datasetPreview')
        this.saveViz()
      }
    },
    changeBaseLayer (baseLayer) {
      if (this.dataset.vizSettings.baseLayer !== baseLayer) {
        this.$store.commit('hideDrawer', 'datasetDetails')
        this.dataset.vizSettings.baseLayer = baseLayer
        this.saveViz()
        setTimeout(() => {
          this.$store.dispatch('launchDataset', { dataset: this.dataset })
        }, 300)
      }
    },
    saveViz () {
      let datasetEditor = false
      if (this.dataset.editors && this.dataset.editors.includes(this.$store.state.currentUser.uid)) {
        datasetEditor = true
      }
      if (this.preventSave || (this.$store.state.role !== 'Administrator' && this.$store.state.role !== 'Editor' && !datasetEditor)) {
        console.log('Viz not saved')
      } else {
        fb.datasetsCollection.doc(this.dataset.id).update({
          vizSettings: this.dataset.vizSettings
        }).catch(err => {
          console.log(err)
        })
      }
    }
  }
}
</script>

<style scoped>
#remove-dataset {
  position: absolute;
  top: 7px;
  right: 7px;
}

.mobile-buffer {
  height: 150px;
}

.color-preset {
  padding-top: 8px;
  margin-right: 4px;
}

.color-chip {
  height: 15px;
  width: 10px;
  display: inline-block;
}

.custom-switch-green ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #056839;
  border-color: #056839;
}

.custom-switch.b-custom-control-lg .custom-control-label, .input-group-lg .custom-switch .custom-control-label {
  font-size: 1rem;
  line-height: 1.8;
}

/* Outline the color-swatch chip so a white/light selection stays visible
   against the white drawer background (otherwise it reads as blank space). */
::v-deep .vue-swatches__trigger {
  border: 1px solid #ced4da;
  box-sizing: border-box;
}
</style>
